import { AcceptanceDataSnapshots, AcceptanceManifest } from './types';
import { buildSnapshotDiff } from './snapshotDiff';

export interface ObserverCheckInput {
  documentKey?: string;
  latestRecords: unknown[];
  auditEntries: unknown[];
  errors: string[];
  snapshots?: AcceptanceDataSnapshots;
}

export interface ObserverCheckResult {
  checkId: string;
  label: string;
  passed: boolean;
  detail: string;
}

const asObject = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' ? value as Record<string, unknown> : null
);

// Formal projections reserve these names for screen state, derived display
// values and query inputs. They are not DB tables or evidence-query targets.
const virtualFieldTables = new Set(['UI_STATE', 'UI_DERIVED', 'UI_QUERY']);

/** Pure, deterministic teeth for the acceptance observer itself. */
export const runObserverChecks = (
  manifest: AcceptanceManifest,
  input: ObserverCheckInput,
): ObserverCheckResult[] => {
  const tableNames = new Set(manifest.tables.map(table => table.table));
  const fieldIds = manifest.fields.map(field => field.fieldId);
  const auditMismatches = input.auditEntries.filter(entry => {
    const row = asObject(entry);
    if (!row) return true;
    if (row.form_id !== manifest.formId) return true;
    return !!input.documentKey && row.doc_key !== input.documentKey;
  });
  const auditRequired = !!input.documentKey && manifest.auditExpectations.length > 0;
  const canCompareApiAndDatabase = (
    input.snapshots?.lastApiResponse !== undefined
    && input.snapshots.lastApiResponse !== null
    && input.snapshots.databaseRecord !== undefined
    && input.snapshots.databaseRecord !== null
  );
  const apiDatabaseMismatches = canCompareApiAndDatabase
    ? buildSnapshotDiff(
      input.snapshots?.screenDraft,
      input.snapshots?.lastApiResponse,
      input.snapshots?.databaseRecord,
    ).filter(row => row.status === 'api_db_mismatch')
    : [];

  return [
    {
      checkId: 'manifest-field-id-unique',
      label: '欄位穩定 ID 不重複',
      passed: new Set(fieldIds).size === fieldIds.length,
      detail: `${fieldIds.length} 個欄位／${new Set(fieldIds).size} 個唯一 ID`,
    },
    {
      checkId: 'manifest-table-whitelist',
      label: '欄位只映射到白名單資料表',
      passed: manifest.fields.every(field => tableNames.has(field.table) || virtualFieldTables.has(field.table)),
      detail: `允許資料表：${Array.from(tableNames).join(', ')}`,
    },
    {
      checkId: 'latest-three-limit',
      label: '最新資料不超過三筆',
      passed: input.latestRecords.length <= 3,
      detail: `實際 ${input.latestRecords.length} 筆`,
    },
    {
      checkId: 'audit-scope',
      label: 'Audit 僅屬於目前表單與單據',
      passed: auditMismatches.length === 0,
      detail: auditMismatches.length ? `${auditMismatches.length} 筆超出範圍` : '範圍一致',
    },
    {
      checkId: 'audit-presence',
      label: '有單據時存在 Contract 要求的 Audit 證據',
      passed: !auditRequired || input.auditEntries.length > 0,
      detail: auditRequired
        ? (input.auditEntries.length ? `${input.auditEntries.length} 筆 Audit` : '缺少 Audit，禁止視為 PASS')
        : '新增草稿或 Contract 未要求 Audit',
    },
    {
      checkId: 'observer-reads',
      label: '觀測 API 全部成功',
      passed: input.errors.length === 0,
      detail: input.errors.length ? input.errors.join('；') : '無讀取錯誤',
    },
    {
      checkId: 'api-database-consistency',
      label: '業務 API 回應與 DB 即時內容一致',
      passed: apiDatabaseMismatches.length === 0,
      detail: canCompareApiAndDatabase
        ? (apiDatabaseMismatches.length ? `${apiDatabaseMismatches.length} 個欄位不一致` : 'API 與 DB 快照一致')
        : '尚無可比較的已載入 API 與 DB 快照',
    },
  ];
};
